import { createFileRoute } from '@tanstack/react-router';
import { z } from 'zod';
import { useState } from 'react';
import { Card, CardHeader, CardTitle, CardContent, CardDescription, CardFooter } from '@/components/ui';
import { Button } from '@/components/ui';
import { AlertCircle, CheckCircle2 } from 'lucide-react';
import { apiClient } from '@/services/api/client';

const searchSchema = z.object({
  token: z.string().optional(),
  scope: z.enum(['QUOTE', 'USER']).optional(),
  userId: z.string().uuid().optional(),
  quoteId: z.string().uuid().optional().nullable(),
  category: z.string().optional(),
  quoteRef: z.string().optional(),
});

export const Route = createFileRoute('/unsubscribe')({
  validateSearch: searchSchema,
  component: UnsubscribePage,
});

function UnsubscribePage() {
  const search = Route.useSearch();
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [errorMessage, setErrorMessage] = useState('');
  
  const isMissingParams = !search.token || !search.scope || !search.userId || !search.category;

  const handleUnsubscribe = async () => {
    try {
      setStatus('loading');
      await apiClient.post('/api/unsubscribe', {
        token: search.token,
        scope: search.scope,
        userId: search.userId,
        quoteId: search.quoteId || null,
        category: search.category,
      });

      setStatus('success');
    } catch (err: any) {
      console.error(err);
      setStatus('error');
      setErrorMessage(err.message || 'An error occurred while unsubscribing.');
    }
  };

  const handleUndo = async () => {
    try {
      setStatus('loading');
      await apiClient.delete('/api/unsubscribe', {
        body: JSON.stringify({ token: search.token }),
      });
      setStatus('idle');
    } catch (err: any) {
      console.error(err);
      setStatus('error');
      setErrorMessage('Failed to undo unsubscribe.');
    }
  };

  const categoryName = search.category ? search.category.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()).join(' ') : 'these emails';

  if (isMissingParams) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-50 p-4">
        <Card className="w-full max-w-md">
          <CardHeader>
            <CardTitle className="flex items-center text-red-600">
              <AlertCircle className="mr-2 h-5 w-5" />
              Invalid Link
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-gray-600">
              This unsubscribe link is invalid or has expired. Please check the link in your email and try again.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-gray-50 p-4">
      <Card className="w-full max-w-md">
        {status === 'success' ? (
          <>
            <CardHeader>
              <CardTitle className="flex items-center text-green-600">
                <CheckCircle2 className="mr-2 h-5 w-5" />
                Unsubscribed Successfully
              </CardTitle>
              <CardDescription>
                You have been unsubscribed from {categoryName}.
              </CardDescription>
            </CardHeader>
            <CardFooter>
              <Button variant="outline" className="w-full" onClick={handleUndo}>
                Undo (Resubscribe)
              </Button>
            </CardFooter>
          </>
        ) : (
          <>
            <CardHeader>
              <CardTitle>Confirm Unsubscribe</CardTitle>
              <CardDescription>
                {search.scope === 'QUOTE' 
                  ? `Are you sure you want to unsubscribe from ${categoryName} for Quote ${search.quoteRef || search.quoteId}?`
                  : `Are you sure you want to unsubscribe from ${categoryName}?`}
              </CardDescription>
            </CardHeader>
            
            {status === 'error' && (
              <CardContent>
                <div className="rounded-md bg-red-50 p-3 text-sm text-red-600 flex items-center">
                  <AlertCircle className="mr-2 h-4 w-4" />
                  {errorMessage}
                </div>
              </CardContent>
            )}

            <CardFooter>
              <Button 
                variant="primary" 
                className="w-full bg-red-600 hover:bg-red-700 text-white" 
                onClick={handleUnsubscribe}
                disabled={status === 'loading'}
              >
                {status === 'loading' ? 'Unsubscribing...' : 'Confirm Unsubscribe'}
              </Button>
            </CardFooter>
          </>
        )}
      </Card>
    </div>
  );
}
